import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateAuthUser, OrganizationMember } from "@/lib/api/betterAuth";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  deriveAuthUserUpdateFields,
  AuthUserUpdatableFields,
} from "../utils/authUserUpdate";

// name: z.string().transform(item => item?.trim() ? item : null).optional().nullable(),
const UpdateUserFormValidation = z.object({
  name: z.string().min(1, "Name is required"),
});

type FormData = z.infer<typeof UpdateUserFormValidation>;

interface UpdateUserModalProps {
  open: boolean;
  onClose: () => void;
  member: OrganizationMember | null | undefined;
}

export function UpdateUserModal({
  open,
  onClose,
  member,
}: UpdateUserModalProps) {
  const { toast } = useToast();
  const updateUserMutation = useUpdateAuthUser();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(UpdateUserFormValidation),
    defaultValues: {
      name: member?.authuser?.name ?? "",
    },
  });

  const onSubmit = async (data: FormData) => {
    const authUserId = member?.authuser?.id;
    if (!authUserId) {
      toast({
        title: "Error",
        description: "No auth user ID found for this member",
        variant: "destructive",
      });
      return;
    }

    const updatedFields: AuthUserUpdatableFields = {
      name: data.name?.trim(),
    };

    // Derive only the changed fields
    const updatePayload = deriveAuthUserUpdateFields(
      member.authuser,
      updatedFields
    );

    if (!updatePayload) {
      toast({
        title: "No Changes",
        description: "No fields were modified",
      });
      return;
    }

    try {
      const result = await updateUserMutation.mutateAsync({
        params: { id: authUserId },
        body: updatePayload,
      });
      if (!result.success) {
        throw result;
      }

      toast({
        title: "Success",
        description: "User updated successfully",
      });

      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to update user",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset({ name: "" });
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              {...register("name")}
              placeholder="Enter user name"
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name.message}</p>
            )}
          </div>

          {member?.authuser?.phoneNumber && (
            <div className="space-y-2">
              <Label className="text-muted-foreground">Phone Number</Label>
              <p className="text-sm text-muted-foreground">
                {member.authuser.phoneNumber}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

